// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { transitions, hoverEffects, focusEffects, microInteractions } from '../utils/animations';
import { makeAccessible, touch } from '../utils/accessibility';
import { LoadingSpinner } from './LoadingState';

/**
 * Fully Accessible Button Component
 * Includes ARIA labels, keyboard navigation, loading states, and animations
 */

const AccessibleButton = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  ariaLabel,
  type = 'button',
  className = '',
  ...props
}) => {
  // Variant styles
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700',
    secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
    success: 'bg-green-600 text-white hover:bg-green-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    warning: 'bg-orange-600 text-white hover:bg-orange-700',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50',
    ghost: 'text-gray-700 hover:bg-gray-100',
  };

  // Size styles
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    xl: 'px-8 py-4 text-xl',
  };

  // Build class names
  const buttonClasses = [
    'inline-flex items-center justify-center gap-2',
    'font-semibold rounded-lg',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    variant === 'primary' ? 'focus:ring-purple-500' : 'focus:ring-blue-500',
    transitions.default,
    microInteractions.button,
    touch.minTouchTarget,
    variants[variant],
    sizes[size],
    fullWidth ? 'w-full' : '',
    disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    className,
  ].filter(Boolean).join(' ');

  // Accessibility props
  const accessibilityProps = {
    ...makeAccessible.button(ariaLabel || (typeof children === 'string' ? children : 'Button'), {
      disabled: disabled || loading,
    }),
    type,
  };

  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading) {
      e.preventDefault();
      onClick && onClick(e);
    }
  };

  return (
    <button
      className={buttonClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled || loading}
      {...accessibilityProps}
      {...props}
    >
      {loading && (
        <LoadingSpinner size="sm" color={variant === 'primary' ? 'white' : 'blue'} />
      )}
      
      {!loading && icon && iconPosition === 'left' && (
        <span aria-hidden="true">{icon}</span>
      )}
      
      <span>{children}</span>
      
      {!loading && icon && iconPosition === 'right' && (
        <span aria-hidden="true">{icon}</span>
      )}
    </button>
  );
};

export default AccessibleButton;
